/**
 * BenutzerPage — CRM-Benutzerverwaltung
 *
 * Aufgabe 3: CRUD für CRM-Admin-Benutzer.
 * Endpoints: GET /v1/admin/all-admins (Liste), GET/PUT /v1/admin/admin/{id} (Detail/Update)
 * RBAC-Hinweis: Die Seite ist nur via Sidebar erreichbar wenn der eingeloggte Admin
 * die entsprechende Role hat. Serverseitig schützt Laravel Passport die Endpoints.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Card, Drawer, Field, Input, MultiSelect, SectionTitle } from '../../components/ui';
import { Icon } from '../../components/icons';
import { BRAND, getThemeColors } from '../../lib/theme';
import { formatDate } from '../../lib/dateFormat';
import { v1Admins, type V1Admin, type V1AdminUpdatePayload, type V1AdminRole } from '../../lib/v1';
import { departmentsApi, loadMyDepartments, type Department } from '../../lib/departments';
import { useOptionalAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { apiErrorMessage } from '../einstellungen/abteilungen/shared';
import type { Density } from '../../types';

interface Props {
  dark: boolean;
  density?: Density;
}

interface AdminForm {
  vorname: string;
  nachname: string;
  email: string;
  mobile: string;
  role: string;
  konto: string;
  password: string;
}

const EMPTY_FORM: AdminForm = {
  vorname: '', nachname: '', email: '', mobile: '', role: '2', konto: '1', password: '',
};

function adminToForm(a: V1Admin): AdminForm {
  return {
    vorname:  a.vorname  ?? '',
    nachname: a.nachname ?? '',
    email:    a.email    ?? '',
    mobile:   a.mobile   ?? '',
    role:     String(a.role ?? 2),
    konto:    String(a.konto ?? 1),
    password: '',
  };
}

export function BenutzerPage({ dark }: Props) {
  const { t } = useTranslation();
  const tc = getThemeColors(dark);
  const auth = useOptionalAuth();
  const { refresh: refreshPermissions } = usePermissions();

  const [admins, setAdmins]         = useState<V1Admin[]>([]);
  const [roles, setRoles]           = useState<V1AdminRole[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [search, setSearch]         = useState('');
  const [roleFilter, setRoleFilter]   = useState<string>('Alle');
  const [kontoFilter, setKontoFilter] = useState<string>('Alle');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing]       = useState<V1Admin | null>(null); // null = neu
  const [form, setForm]             = useState<AdminForm>(EMPTY_FORM);
  const [saving, setSaving]         = useState(false);
  const [saveError, setSaveError]   = useState<string | null>(null);
  const [deleteId, setDeleteId]     = useState<number | null>(null);
  // Abteilungen + persönliche Rollen (nur sichtbar, wenn der Admin Abteilungen verwalten darf)
  const [deptList, setDeptList]     = useState<Department[] | null>(null);
  const [deptIds, setDeptIds]       = useState<number[]>([]);
  const [extraRoleIds, setExtraRoleIds] = useState<number[]>([]);
  const [deptLoaded, setDeptLoaded] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    v1Admins.list()
      .then(setAdmins)
      .catch(() => setError(t('benutzer.error.load')))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => { load(); }, [load]);

  // Echte Rollen aus admin_roles laden — Namen müssen mit „Rollen & Rechte"
  // übereinstimmen (statt hartkodierter „Rolle 1..5").
  useEffect(() => {
    v1Admins.roles().then(setRoles).catch(() => {/* dropdown fällt auf Fallback zurück */});
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setSaveError(null);
    setDrawerOpen(true);
  };

  const openEdit = (a: V1Admin) => {
    setEditing(a);
    setForm(adminToForm(a));
    setSaveError(null);
    setDrawerOpen(true);
    setDeptLoaded(false);
    setDeptIds([]);
    setExtraRoleIds([]);
    Promise.all([departmentsApi.list(), departmentsApi.adminDepartments(a.id), departmentsApi.adminExtraRoles(a.id)])
      .then(([list, ids, extra]) => { setDeptList(list); setDeptIds(ids); setExtraRoleIds(extra); setDeptLoaded(true); })
      .catch(() => { setDeptList(null); setDeptLoaded(false); }); // ohne Recht: Abschnitt bleibt aus
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditing(null);
    setSaveError(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    const payload: V1AdminUpdatePayload = {
      vorname:  form.vorname,
      nachname: form.nachname,
      email:    form.email,
      mobile:   form.mobile || null,
      role:     Number(form.role),
      konto:    Number(form.konto),
    };
    try {
      if (editing) {
        // Passwort nur mitsenden, wenn der Operator ein neues gesetzt hat.
        await v1Admins.update(editing.id, form.password ? { ...payload, password: form.password } : payload);
      } else {
        await v1Admins.create({ ...payload, password: form.password });
      }
    } catch {
      setSaveError(t('benutzer.error.save'));
      setSaving(false);
      return;
    }

    // Abteilungen und persönliche Rollen getrennt speichern; Teilergebnis klar melden.
    if (editing && deptLoaded) {
      const saved: string[] = [t('benutzer.departments.part_base')];
      const failed: Array<{ part: string; reason: string }> = [];
      try {
        await departmentsApi.setAdminDepartments(editing.id, deptIds);
        saved.push(t('benutzer.departments.label'));
      } catch (e) {
        failed.push({ part: t('benutzer.departments.label'), reason: apiErrorMessage(e, t('benutzer.error.save')) });
      }
      try {
        await departmentsApi.setAdminExtraRoles(editing.id, extraRoleIds);
        saved.push(t('benutzer.departments.personal_roles'));
      } catch (e) {
        failed.push({ part: t('benutzer.departments.personal_roles'), reason: apiErrorMessage(e, t('benutzer.error.save')) });
      }
      // Eigene Rechte/Menü sofort neu laden, wenn der eigene Account betroffen ist.
      if (auth?.user?.id === editing.id) {
        refreshPermissions();
        void loadMyDepartments(true);
      }
      if (failed.length > 0) {
        setSaveError(t('benutzer.departments.partial', {
          saved: saved.join(', '),
          failed: failed.map(f => `${f.part} (${f.reason})`).join('; '),
        }));
        setSaving(false);
        load();
        return;
      }
    }
    setSaving(false);
    closeDrawer();
    load();
  };

  const handleDelete = async (id: number) => {
    try {
      await v1Admins.delete(id);
      setDeleteId(null);
      load();
    } catch {
      // show inline error
    }
  };

  const filtered = admins.filter((a) => {
    if (roleFilter !== 'Alle' && String(a.role) !== roleFilter) return false;
    if (kontoFilter !== 'Alle' && String(a.konto) !== kontoFilter) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      String(a.id).includes(q) ||
      (a.email ?? '').toLowerCase().includes(q) ||
      ((a.vorname ?? '') + ' ' + (a.nachname ?? '')).toLowerCase().includes(q)
    );
  });

  const setF = (k: keyof AdminForm, v: string) => setForm((f) => ({ ...f, [k]: v }));

  // Echte Rollennamen (admin_roles) bevorzugen; Fallback auf i18n/„Rolle N".
  const roleLabel = (role: number) =>
    roles.find(r => r.id === role)?.name ?? t(`benutzer.role.${role}`, { defaultValue: `Rolle ${role}` });
  const kontoLabel = (konto: number) => t(`benutzer.konto.${konto}`, { defaultValue: `Status ${konto}` });

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <SectionTitle
        dark={dark}
        title={t('benutzer.title')}
        subtitle={t('benutzer.subtitle')}
      />

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 340 }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
            <Icon.Search c={tc.muted} />
          </span>
          <Input
            dark={dark}
            style={{ paddingLeft: 34 }}
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            placeholder={t('benutzer.list.search.placeholder')}
          />
        </div>
        <select
          aria-label={t('benutzer.filter.role')}
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          style={{ padding: '8px 10px', borderRadius: 8, border: `1px solid ${tc.border}`, background: tc.inputBg, color: tc.text, fontSize: 13, fontFamily: 'Outfit,sans-serif', cursor: 'pointer' }}
        >
          <option value="Alle">{t('benutzer.filter.role_all')}</option>
          {(roles.length ? roles.map(r => r.id) : [1, 2, 3, 4, 5]).map((r) => (
            <option key={r} value={String(r)}>{roleLabel(r)}</option>
          ))}
        </select>
        <select
          aria-label={t('benutzer.filter.konto')}
          value={kontoFilter}
          onChange={(e) => setKontoFilter(e.target.value)}
          style={{ padding: '8px 10px', borderRadius: 8, border: `1px solid ${tc.border}`, background: tc.inputBg, color: tc.text, fontSize: 13, fontFamily: 'Outfit,sans-serif', cursor: 'pointer' }}
        >
          <option value="Alle">{t('benutzer.filter.konto_all')}</option>
          {[1, 2, 3].map((k) => (
            <option key={k} value={String(k)}>{kontoLabel(k)}</option>
          ))}
        </select>
        <Button dark={dark} variant="primary" icon={<Icon.Plus />} onClick={openNew}>
          {t('benutzer.action.new')}
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div style={{ color: '#d92d20', marginBottom: 12, fontSize: 13 }}>{error}</div>
      )}

      {/* Table */}
      <Card dark={dark} style={{ overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${tc.border}` }}>
              {(['id', 'name', 'email', 'role', 'status', 'since'] as const).map((col) => (
                <th
                  key={col}
                  style={{
                    textAlign: 'left', padding: '10px 14px',
                    color: tc.muted, fontWeight: 600, fontSize: 11,
                    letterSpacing: '0.05em', textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t(`benutzer.list.header.${col}`)}
                </th>
              ))}
              <th style={{ width: 80 }} />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} style={{ padding: '24px 14px', textAlign: 'center', color: tc.muted }}>
                  {t('benutzer.list.loading')}
                </td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: '24px 14px', textAlign: 'center', color: tc.muted }}>
                  {t('benutzer.list.empty')}
                </td>
              </tr>
            )}
            {filtered.map((a) => (
              <tr
                key={a.id}
                onClick={() => openEdit(a)}
                style={{ borderBottom: `1px solid ${tc.border}`, cursor: 'pointer' }}
                onMouseEnter={(e) => ((e.currentTarget as HTMLTableRowElement).style.background = tc.hover)}
                onMouseLeave={(e) => ((e.currentTarget as HTMLTableRowElement).style.background = 'transparent')}
              >
                <td style={{ padding: '10px 14px', color: tc.muted, fontVariantNumeric: 'tabular-nums' }}>
                  #{a.id}
                </td>
                <td style={{ padding: '10px 14px', color: tc.text, fontWeight: 500 }}>
                  {[a.vorname, a.nachname].filter(Boolean).join(' ') || '—'}
                </td>
                <td style={{ padding: '10px 14px', color: tc.muted }}>{a.email}</td>
                <td style={{ padding: '10px 14px' }}>
                  <Badge dark={dark} tone="neutral">{roleLabel(a.role)}</Badge>
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <Badge dark={dark} tone={a.konto === 1 ? 'success' : 'warning'}>
                    {kontoLabel(a.konto)}
                  </Badge>
                </td>
                <td style={{ padding: '10px 14px', color: tc.muted, fontSize: 12 }}>
                  {formatDate(a.created_at)}
                </td>
                <td style={{ padding: '10px 14px' }} onClick={(e) => e.stopPropagation()}>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button
                      title={t('benutzer.action.edit')}
                      onClick={() => openEdit(a)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                    >
                      <Icon.Edit c={tc.muted} />
                    </button>
                    <button
                      title={t('benutzer.action.delete')}
                      onClick={() => setDeleteId(a.id)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                    >
                      <Icon.Trash c="#d92d20" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Edit / Create Drawer */}
      <Drawer dark={dark} open={drawerOpen} onClose={closeDrawer} width={440}>
        <div style={{ padding: '24px 24px 16px', borderBottom: `1px solid ${tc.border}`, flexShrink: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: tc.heading, marginBottom: 4 }}>
            {editing ? t('benutzer.form.title_edit') : t('benutzer.form.title_new')}
          </div>
          {editing && (
            <div style={{ fontSize: 12, color: tc.muted }}>
              #{editing.id} · {editing.email}
            </div>
          )}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label={t('benutzer.form.field.vorname')}>
              <Input dark={dark} value={form.vorname} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setF('vorname', e.target.value)} placeholder={t('benutzer.form.field.vorname')} />
            </Field>
            <Field label={t('benutzer.form.field.nachname')}>
              <Input dark={dark} value={form.nachname} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setF('nachname', e.target.value)} placeholder={t('benutzer.form.field.nachname')} />
            </Field>
          </div>
          <Field label={t('benutzer.form.field.email')}>
            <Input dark={dark} value={form.email} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setF('email', e.target.value)} placeholder="name@senihelp24.de" type="email" />
          </Field>
          <Field label={t('benutzer.form.field.mobile')}>
            <Input dark={dark} value={form.mobile} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setF('mobile', e.target.value)} placeholder="+49 170 1234567" />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label={t('benutzer.form.field.role')}>
              <select
                value={form.role}
                onChange={(e) => setF('role', e.target.value)}
                style={{
                  width: '100%', padding: '8px 10px', borderRadius: 8,
                  border: `1px solid ${tc.border}`, background: tc.inputBg,
                  color: tc.text, fontSize: 13, fontFamily: 'Outfit,sans-serif',
                }}
              >
                {(roles.length ? roles.map(r => r.id) : [1, 2, 3, 4, 5]).map((r) => (
                  <option key={r} value={r}>{roleLabel(r)}</option>
                ))}
              </select>
            </Field>
            <Field label={t('benutzer.form.field.konto')}>
              <select
                value={form.konto}
                onChange={(e) => setF('konto', e.target.value)}
                style={{
                  width: '100%', padding: '8px 10px', borderRadius: 8,
                  border: `1px solid ${tc.border}`, background: tc.inputBg,
                  color: tc.text, fontSize: 13, fontFamily: 'Outfit,sans-serif',
                }}
              >
                {[1, 2, 3].map((k) => (
                  <option key={k} value={k}>{kontoLabel(k)}</option>
                ))}
              </select>
            </Field>
          </div>
          <Field
            label={t('benutzer.form.field.password')}
            hint={editing ? t('benutzer.form.field.password_hint_edit') : t('benutzer.form.field.password_hint')}
          >
            <Input dark={dark} value={form.password} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setF('password', e.target.value)} type="password" placeholder={editing ? t('benutzer.form.field.password_placeholder_edit') : '••••••••'} autoComplete="new-password" />
          </Field>

          {editing && deptLoaded && deptList && (
            <>
              <Field label={t('benutzer.departments.label')}>
                <MultiSelect
                  dark={dark}
                  options={deptList.map(d => ({ id: d.id, label: d.name }))}
                  value={deptIds}
                  onChange={setDeptIds}
                  placeholder={t('benutzer.departments.add')}
                  ariaLabel={t('benutzer.departments.label')}
                  removeLabel={n => t('departments.common.remove_named', { name: n })}
                />
              </Field>
              <Field label={t('benutzer.departments.roles_from')} hint={t('benutzer.departments.roles_from_hint')}>
                <div data-testid="roles-from-departments" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {deptList
                    .filter(d => deptIds.includes(d.id))
                    .flatMap(d => d.role_ids.map(rid => ({ key: `${d.id}-${rid}`, role: roles.find(r => r.id === rid)?.name ?? `#${rid}`, dept: d.name })))
                    .map(x => (
                      <Badge key={x.key} dark={dark} tone="neutral">{x.role} · {t('benutzer.departments.via', { name: x.dept })}</Badge>
                    ))}
                  {deptList.filter(d => deptIds.includes(d.id)).every(d => d.role_ids.length === 0) && (
                    <span style={{ fontSize: 12, color: tc.muted }}>{t('benutzer.departments.no_roles')}</span>
                  )}
                </div>
              </Field>
              <Field label={t('benutzer.departments.personal_roles')} hint={t('benutzer.departments.personal_roles_hint')}>
                <MultiSelect
                  dark={dark}
                  options={roles.map(r => ({ id: r.id, label: r.name }))}
                  value={extraRoleIds}
                  onChange={setExtraRoleIds}
                  placeholder={t('benutzer.departments.add_role')}
                  ariaLabel={t('benutzer.departments.personal_roles')}
                  removeLabel={n => t('departments.common.remove_named', { name: n })}
                />
              </Field>
            </>
          )}

          {saveError && (
            <div style={{ color: '#d92d20', fontSize: 12 }}>{saveError}</div>
          )}
        </div>

        <div style={{
          padding: '14px 24px', borderTop: `1px solid ${tc.border}`,
          display: 'flex', gap: 10, justifyContent: 'flex-end', flexShrink: 0,
        }}>
          <Button dark={dark} variant="ghost" onClick={closeDrawer}>
            {t('benutzer.action.cancel')}
          </Button>
          <Button dark={dark} variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? t('benutzer.action.saving') : t('benutzer.action.save')}
          </Button>
        </div>
      </Drawer>

      {/* Delete Confirm Modal */}
      {deleteId !== null && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => setDeleteId(null)}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: tc.card, border: `1px solid ${tc.border}`, borderRadius: 12,
              padding: 28, maxWidth: 400, width: '90%',
            }}
          >
            <div style={{ fontSize: 15, fontWeight: 700, color: tc.heading, marginBottom: 10 }}>
              {t('benutzer.delete.confirm_title')}
            </div>
            <div style={{ fontSize: 13, color: tc.muted, marginBottom: 20 }}>
              {t('benutzer.delete.confirm_body')}
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Button dark={dark} variant="ghost" onClick={() => setDeleteId(null)}>
                {t('benutzer.action.cancel')}
              </Button>
              <Button
                dark={dark}
                style={{ background: '#d92d20', borderColor: '#d92d20', color: '#fff' }}
                onClick={() => handleDelete(deleteId)}
              >
                {t('benutzer.delete.confirm_action')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
